import { Stack } from '@mui/material';
import React from 'react';
import type { FilterField, FilterSection } from '../../../api/models/filters.model';
import ActiveFilterChip from './ActiveFilterChipComponent';

interface ActiveFiltersBarProps {
	filterSections: FilterSection[];
	filterValues: { [key: string]: any };
	handleRemoveFilter: (
		field: FilterField,
		value?: any,
		rangeType?: 'min' | 'max' | 'range'
	) => void;
}

const ActiveFiltersBar: React.FC<ActiveFiltersBarProps> = ({
	filterSections,
	filterValues,
	handleRemoveFilter,
}) => {
	const chips = React.useMemo(() => {
		const result: React.ReactNode[] = [];
		filterSections.forEach((section) => {
			section.fields.forEach((field) => {
				if ((field.type === 'string' || field.type === 'chip') && field.values) {
					const selected = filterValues[field.name];
					if (Array.isArray(selected)) {
						selected.forEach((val: string) => {
							result.push(
								<ActiveFilterChip
									key={`${field.name}-${val}`}
									chipKey={`${field.name}-${val}`}
									label={`${field.label}: ${val}`}
									onDelete={() => handleRemoveFilter(field, val)}
								/>
							);
						});
					} else if (typeof selected === 'string' || typeof selected === 'number') {
						result.push(
							<ActiveFilterChip
								key={`${field.name}-${selected}`}
								chipKey={`${field.name}-${selected}`}
								label={`${field.label}: ${selected}`}
								onDelete={() => handleRemoveFilter(field, selected)}
							/>
						);
					}
				} else if (field.type === 'object') {
					const selected = filterValues[field.name];
					if (Array.isArray(selected)) {
						selected.forEach((account: { sourceId: number; sourceName: string }) => {
							result.push(
								<ActiveFilterChip
									key={`${field.name}-${account.sourceId}`}
									chipKey={`${field.name}-${account.sourceId}`}
									label={`${field.label}: ${account.sourceName}`}
									onDelete={() => handleRemoveFilter(field, account)}
								/>
							);
						});
					}
				} else if (field.type === 'number' || field.type === 'date') {
					const min = filterValues[`${field.name}_min`];
					const max = filterValues[`${field.name}_max`];
					if (min && max) {
						result.push(
							<ActiveFilterChip
								key={`${field.name}-range`}
								chipKey={`${field.name}-${min}-${max}`}
								label={`${field.label}: ${min} - ${max}`}
								onDelete={() => handleRemoveFilter(field, undefined, 'range')}
							/>
						);
					} else if (min) {
						result.push(
							<ActiveFilterChip
								key={`${field.name}-min`}
								chipKey={`${field.name}-${min}`}
								label={`${field.label}: ${min} - Max`}
								onDelete={() => handleRemoveFilter(field, undefined, 'min')}
							/>
						);
					} else if (max) {
						result.push(
							<ActiveFilterChip
								key={`${field.name}-max`}
								chipKey={`${field.name}-${max}`}
								label={`${field.label}: Min - ${max}`}
								onDelete={() => handleRemoveFilter(field, undefined, 'max')}
							/>
						);
					}
				}
			});
		});
		return result;
	}, [filterSections, filterValues, handleRemoveFilter]);
	if (chips.length === 0) return null;
	return (
		<div style={{ margin: '8px 0' }}>
			<Stack direction="row" flexWrap="wrap">
				{chips}
			</Stack>
		</div>
	);
};

export default ActiveFiltersBar;
